import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { BodyText, Button, theme, useNoticeBannersStackContext } from '@sg/garnish';

// ────────────────────────────────────────────────────────────────────────────

export const BagCheckoutCta = (props: BagCheckoutCtaProps) => {
  const { cta, paymentMethodId, isLoading, isDisabled, isFloating, onCheckout } = props;

  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();
  const { bottom } = useSafeAreaInsets();
  const floatingBottomStyle = useStyle(
    () => (isFloating ? { bottom: bottom + theme.spacing['4'] } : undefined),
    [isFloating, bottom],
  );

  const paymentMethodMissingWarning = formatMessage(messages.paymentMethodMissingWarning);

  const floatingStyle = isFloating ? styles.checkoutCtaFloating : undefined;
  const defaultText = isFloating ? styles.checkoutCtaFloatingText : styles.checkoutCtaLabel;
  const ctaStyle = isDisabled ? styles.checkoutCtaDisabled : defaultText;

  const handleOnPress = useCallback(() => {
    if (!paymentMethodId) {
      addNoticeBanner({
        text: paymentMethodMissingWarning,
        palette: 'caution',
      });
      return;
    }

    onCheckout();
  }, [paymentMethodId, paymentMethodMissingWarning, onCheckout, addNoticeBanner]);

  return (
    <View style={[styles.checkoutCtaContainer, floatingStyle, floatingBottomStyle]}>
      <Button
        palette="primaryLime"
        size="large"
        style={styles.checkoutCta}
        isLoading={isLoading}
        disabled={Boolean(isDisabled && !isLoading)}
        accessibilityLabel={cta}
        onPress={handleOnPress}
      >
        <BodyText sizeMatch={['18']} style={ctaStyle}>
          {cta}
        </BodyText>
      </Button>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  googlePay: {
    defaultMessage: 'Pay with',
    description: 'Bag | CTAs | Google Pay',
  },
  paymentMethodMissingWarning: {
    defaultMessage: 'Please add a payment method to place your order',
    description: 'Bag | Payment method missing warning',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  checkoutCtaContainer: {
    marginHorizontal: theme.spacing['4'],
    minHeight: theme.spacing['16'],
  },
  checkoutCta: {
    minHeight: theme.spacing['16'],
  },
  checkoutCtaLabel: {
    color: theme.colors.LIME,
  },
  checkoutCtaDisabled: {
    color: theme.colors.GRAY,
  },
  checkoutCtaFloating: {
    position: 'absolute',
    bottom: theme.spacing['4'],
    left: 0,
    right: 0,
  },
  checkoutCtaFloatingText: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagCheckoutCtaProps = {
  cta: string;
  paymentMethodId: string | undefined;
  isLoading: boolean;
  isDisabled: boolean;
  isFloating?: boolean;
  onCheckout: () => void;
};
